import { hasSellarCreditPaymentAttempt } from "./sellarCreditPaymentAttempt";
import { twMerge } from "tailwind-merge";
import { Button, DataButton, Icon } from "@sellar-io/components";
import { usePaymentList } from "../../../hooks/api/v2/payment/usePaymentList";
import dayjs from "dayjs";
import { useState } from "react";
import { useOrderCancel } from "../../../hooks/api/v2/order/v2/useOrderCancel";
import { ToastProvider } from "../../toast/useToast";
import { useToast } from "../../toast/useToast.shared";
import { Toast } from "../../toast/Toast";
import { PayWithSellarCreditButton } from "../../../pages/stockList/checkoutV2/lib/PayWithSellarCreditButton";
import { useSellarCreditPaymentStatus } from "../useSellarCreditPaymentStatus";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";

type SellarCreditAddToTabButtonProps = {
  orderId: number;
  orderDeferredPaymentId?: string | null;
  initiator: "supplier" | "retailer" | null;
  buttonSize?: "small" | "large";
  isDisabled?: boolean;
  className?: string;
  ctaText?: string;
  appearance?: "slim";
};
export const SellarCreditAddToTabButton = (
  props: SellarCreditAddToTabButtonProps,
) => {
  return (
    <ToastProvider>
      <SellarCreditAddToTabButtonContent {...props} />
    </ToastProvider>
  );
};

const SellarCreditAddToTabButtonContent = ({
  orderId,
  orderDeferredPaymentId,
  initiator,
  buttonSize,
  className,
  isDisabled = false,
  ctaText,
  appearance,
}: SellarCreditAddToTabButtonProps) => {
  const { toast } = useToast();
  const orderCancelMutation = useOrderCancel();
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const orderQuery = useOrder(orderId, {
    enabled: Boolean(orderId),
  });

  const twoOrder = orderQuery.data?.TwoOrder;

  const paymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: orderId,
        },
      ],
      limit: 1,
    },
    {
      enabled: !orderDeferredPaymentId,
      refetchInterval: (response) => {
        const data = response.state.data?.pages[0]?.[0];
        const timeSinceCreated = dayjs(data?.createdAt).diff(
          dayjs(),
          "minutes",
        );
        return !data?.deferredPaymentId && timeSinceCreated < 10
          ? 10000
          : false;
      },
    },
  );
  const { isProcessed } = useSellarCreditPaymentStatus({
    orderId,
  });

  const handleCancelOrder = async ({
    orderId,
    onSuccess,
  }: {
    orderId: number;
    onSuccess: () => void;
  }) => {
    setIsCancellingOrder(true);
    await orderCancelMutation.mutateAsync(
      {
        id: orderId,
        data: {
          cancellationReason: "Could not place order with Sellar Credit",
          cancelledAt: new Date(),
          cancelledBy: "retailer",
          orderComment: null,
        },
      },
      {
        onSuccess: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="CHECK_CIRCLE">Order added to tab successfully</Toast>
            ),
          });
          onSuccess();
        },
        onError: () => {
          setIsCancellingOrder(false);
          const message = `Could not cancel your order at this time, please try again.`;
          toast({
            placement: "bottom",
            render: () => <Toast icon="ALERT">{message}</Toast>,
          });
        },
      },
    );
  };

  if (isProcessed) {
    return null;
  }

  const isSupplierInitiated = initiator === "supplier";

  const buttonText = () => {
    if (ctaText) {
      return ctaText;
    }
    const hasPaymentBeenInitiated = hasSellarCreditPaymentAttempt({
      twoOrder,
      hasPayment: Boolean(paymentsQuery.data?.pages[0]?.[0]),
    });
    const isButtonSmall = buttonSize === "small";

    if (isButtonSmall && hasPaymentBeenInitiated) return "Retry";
    if (isButtonSmall && !hasPaymentBeenInitiated) return "Add to Tab";

    if (isSupplierInitiated && hasPaymentBeenInitiated)
      return "Retry with sellar credit";
    if (isSupplierInitiated && !hasPaymentBeenInitiated) return "Add to Tab";

    return "Pay with Sellar Credit";
  };

  return (
    <PayWithSellarCreditButton
      orderId={orderId}
      renderTrigger={({ loading, disabled, onClick }) => {
        if (appearance === "slim") {
          return (
            <DataButton
              border
              size="slim"
              className={className}
              onClick={onClick}
              disabled={disabled || isDisabled}
              loading={loading}
            >
              {buttonText()}
            </DataButton>
          );
        }
        return (
          <Button
            onClick={onClick}
            className={twMerge("w-full md:w-min", className)}
            disabled={disabled || isDisabled}
            loading={loading}
            size={buttonSize}
          >
            <Icon icon="SELLAR_NEW" className="text-white" />
            {buttonText()}
          </Button>
        );
      }}
      cancelOrderButtonProps={{
        loading: isCancellingOrder,
        disabled: isCancellingOrder,
        onClick: (orderId) => {
          handleCancelOrder({
            orderId,
            onSuccess: () => {
              window.location.reload();
            },
          });
        },
      }}
      onSuccess={(response) => {
        if (response.paymentUrl) {
          window.location.replace(response.paymentUrl);
          return;
        }
        window.location.reload();
      }}
      onError={() => {
        // Rely on internal error handling alone
      }}
    />
  );
};
